import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';

/**
 * OAuth Popup Callback Component
 *
 * This component runs in the OAuth popup window and handles the callback from Intervals.icu.
 * It exchanges the authorization code for a token and communicates the result back to the parent window.
 */
const OAuthPopupCallback: React.FC = () => {
  const hasProcessed = useRef(false);

  useEffect(() => {
    try {
      // Add global error handler for the popup window
      const handleWindowError = (event: ErrorEvent) => {
        console.error('Popup window error:', event.error);

        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'OAUTH_ERROR',
              error: `Popup window error: ${event.message}`,
            },
            window.location.origin
          );
        }
      };

      window.addEventListener('error', handleWindowError);
      const handleCallback = async () => {
        // Prevent double execution in development mode (React StrictMode)
        if (hasProcessed.current) {
          return;
        }
        hasProcessed.current = true;

        // Debug: Log popup state
        console.log('OAuth callback: window.opener exists:', !!window.opener);
        console.log('OAuth callback: URL:', window.location.href);

        // Immediately notify parent that callback component has loaded
        if (window.opener) {
          try {
            window.opener.postMessage(
              {
                type: 'OAUTH_CALLBACK_LOADED',
                url: window.location.href,
              },
              window.location.origin
            );
          } catch (e) {
            console.warn('Could not post message to opener (may be cross-origin):', e);
          }
        }

        try {
          const urlParams = new URLSearchParams(window.location.search);
          const code = urlParams.get('code');
          const state = urlParams.get('state');
          const token = urlParams.get('token');
          const error = urlParams.get('error');

          if (error) {
            // Send error to parent window
            if (window.opener) {
              window.opener.postMessage(
                {
                  type: 'OAUTH_ERROR',
                  error: decodeURIComponent(error),
                },
                window.location.origin
              );
              window.close();
            } else {
              window.location.href = `/login?error=${encodeURIComponent(error)}`;
            }
            return;
          }

          // Handle direct token from backend redirect (new flow)

          if (token) {
            try {
              // Decode the base64 token data
              const decodedToken = decodeURIComponent(token);
              const tokenData = JSON.parse(atob(decodedToken));

              // Store token data in localStorage for the parent window to pick up
              // This works even if window.opener is lost after redirect
              localStorage.setItem(
                'intervals_oauth_result',
                JSON.stringify({
                  type: 'success',
                  tokenData: tokenData,
                  timestamp: Date.now(),
                })
              );

              // Also store the token itself for persistence
              localStorage.setItem(
                'intervals_token',
                JSON.stringify({
                  accessToken: tokenData.access_token,
                  scope: tokenData.scope,
                  athlete: tokenData.athlete,
                  expiresAt:
                    Date.now() +
                    (tokenData.expires_in
                      ? tokenData.expires_in * 1000
                      : 365 * 24 * 60 * 60 * 1000),
                  timestamp: Date.now(),
                })
              );

              // Try to send message to parent window if available
              if (window.opener) {
                try {
                  window.opener.postMessage(
                    {
                      type: 'OAUTH_SUCCESS',
                      tokenData: tokenData,
                    },
                    window.location.origin
                  );
                } catch (messageError) {
                  console.warn('Could not post message to opener:', messageError);
                }
              }

              // Close the popup window
              console.log('✅ OAuth success, closing popup...');

              // Try to close immediately
              window.close();

              // Fallback after a short delay - if still open, show success message
              setTimeout(() => {
                try {
                  document.body.innerHTML = `
                    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;text-align:center;padding:20px;background:#f5f5f5;">
                      <div style="font-size:48px;margin-bottom:16px;">✅</div>
                      <h2 style="color:#2e7d32;margin:0 0 16px 0;">Connected Successfully!</h2>
                      <p style="color:#666;margin:0 0 24px 0;">Your Intervals.icu account is now connected.</p>
                      <button onclick="window.close()" style="padding:12px 24px;font-size:16px;cursor:pointer;background:#1976d2;color:white;border:none;border-radius:8px;font-weight:500;">
                        Close This Window
                      </button>
                      <p style="color:#999;font-size:12px;margin-top:16px;">If the window doesn't close, please close it manually.</p>
                    </div>
                  `;
                } catch {
                  // Window is closed, all good
                }
              }, 500);

              return;
            } catch (decodeError) {
              console.error('Failed to decode token:', decodeError);
              throw new Error('Failed to decode token data');
            }
          }

          // Handle code exchange flow (original popup flow)
          if (!code || !state) {
            window.opener?.postMessage(
              {
                type: 'OAUTH_ERROR',
                error: 'Missing authorization parameters',
              },
              window.location.origin
            );
            window.close();
            return;
          }

          // Exchange code for token via backend
          const response = await fetch(
            `${import.meta.env.VITE_API_BASE || 'http://localhost:3000'}/auth/intervals/exchange`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ code, state }),
            }
          );

          if (!response.ok) {
            throw new Error(`Token exchange failed: ${response.statusText}`);
          }

          const result = await response.json();

          if (result.success && result.data?.tokenData) {
            // Send success with token to parent window
            window.opener?.postMessage(
              {
                type: 'OAUTH_SUCCESS',
                tokenData: result.data.tokenData,
              },
              window.location.origin
            );

            // Add a small delay before closing to ensure message is delivered
            setTimeout(() => {
              window.close();
            }, 1000);
          } else {
            throw new Error(result.error || 'Token exchange failed');
          }
        } catch (error) {
          console.error('OAuth callback error:', error);

          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'OAUTH_ERROR',
                error: error instanceof Error ? error.message : 'Failed to process OAuth callback',
              },
              window.location.origin
            );
          }
          window.close();
        }
      };

      // Handle the callback immediately when component mounts
      handleCallback().catch(error => {
        console.error('Unhandled error in handleCallback:', error);
      });

      // Cleanup error listener
      return () => {
        window.removeEventListener('error', handleWindowError);
      };
    } catch (useEffectError) {
      console.error('❌ Critical error in useEffect:', useEffectError);
      // Try to send error to parent if possible
      try {
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'OAUTH_ERROR',
              error: `useEffect error: ${useEffectError instanceof Error ? useEffectError.message : String(useEffectError)}`,
            },
            window.location.origin
          );
        }
      } catch (e) {
        console.error('Failed to send useEffect error to parent:', e);
      }
    }
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      gap={2}
    >
      <CircularProgress size={48} />
      <Typography variant="h6">Completing authentication...</Typography>
      <Typography variant="body2" color="text.secondary">
        This window will close automatically.
      </Typography>
    </Box>
  );
};

export default OAuthPopupCallback;
